// Base API configuration and client
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Token refresh state management
let refreshTokenPromise = null;
let isRefreshing = false;

// Token utility functions
export const tokenUtils = {
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  },
  
  getAccessToken: () => {
    return localStorage.getItem('accessToken');
  },
  
  getRefreshToken: () => {
    return localStorage.getItem('refreshToken');
  },
  
  hasTokens: () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    return !!(accessToken && refreshToken);
  },
  
  clearTokens: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Reset refresh state
    refreshTokenPromise = null;
    isRefreshing = false;
  }
};

// Helper function to refresh access token
const refreshAccessToken = async () => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshTokenPromise) {
    return refreshTokenPromise;
  }

  const refreshToken = tokenUtils.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  isRefreshing = true;
  
  refreshTokenPromise = fetch(`${API_BASE_URL}/auth/refresh/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh: refreshToken }),
  })
    .then(async (response) => {
      if (!response.ok) {
        // Refresh token is invalid or expired
        tokenUtils.clearTokens();
        throw new Error('Token refresh failed');
      }
      const data = await response.json();
      if (data.access) {
        tokenUtils.setTokens(data.access, data.refresh || refreshToken);
      }
      return data;
    })
    .finally(() => {
      isRefreshing = false;
      refreshTokenPromise = null;
    });

  return refreshTokenPromise;
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = tokenUtils.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Helper function to handle API responses
const handleResponse = async (response, originalRequest = null) => {
  if (!response.ok) {
    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401 && originalRequest) {
      const refreshToken = tokenUtils.getRefreshToken();
      
      // Only attempt refresh if we have a refresh token and this isn't already a refresh request
      if (refreshToken && !originalRequest.url.includes('/auth/refresh/')) {
        try {
          // Refresh the token
          await refreshAccessToken();
          
          // Retry the original request with new token
          const retryConfig = {
            method: originalRequest.method,
            headers: {
              ...originalRequest.headers,
              'Authorization': `Bearer ${tokenUtils.getAccessToken()}`,
            },
          };
          
          // Preserve body for POST/PUT/PATCH requests
          if (originalRequest.body) {
            retryConfig.body = originalRequest.body;
          }
          
          const retryResponse = await fetch(originalRequest.url, retryConfig);
          
          // If retry succeeds, return the response
          if (retryResponse.ok) {
            try {
              return await retryResponse.json();
            } catch (e) {
              return {};
            }
          } else {
            // Retry also failed - parse and throw error
            let errorData = {};
            try {
              errorData = await retryResponse.json();
            } catch (e) {
              errorData = { error: retryResponse.statusText || `HTTP error! status: ${retryResponse.status}` };
            }
            const error = new Error(errorData.error || errorData.detail || `HTTP error! status: ${retryResponse.status}`);
            error.data = errorData;
            error.status = retryResponse.status;
            throw error;
          }
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          console.error('[API] Token refresh failed:', refreshError);
          tokenUtils.clearTokens();
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          // Re-throw the error so calling code can handle it
          throw refreshError;
        }
      } else {
        // No refresh token or refresh request failed - clear tokens
        tokenUtils.clearTokens();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    
    // Parse error response (only if we haven't already handled it)
    // Note: Response body can only be read once, so we need to clone before reading
    let errorData = {};
    try {
      // Try to read error data - if body was already consumed, this will fail
      const text = await response.text();
      if (text) {
        errorData = JSON.parse(text);
      }
    } catch (e) {
      // If response is not JSON or already consumed, use status text
      errorData = { error: response.statusText || `HTTP error! status: ${response.status}` };
    }
    const error = new Error(errorData.error || errorData.detail || `HTTP error! status: ${response.status}`);
    error.data = errorData;
    error.status = response.status;
    throw error;
  }
  
  try {
    return await response.json();
  } catch (e) {
    // If response is not JSON, return empty object
    console.error('[API] Failed to parse JSON response:', e);
    return {};
  }
};

// API Client object with common HTTP methods
export const apiClient = {
  get: async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const requestConfig = {
      method: 'GET',
      headers: getAuthHeaders(),
      ...options
    };
    const response = await fetch(url, requestConfig);
    return handleResponse(response, { url, ...requestConfig });
  },

  post: async (endpoint, data, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const requestConfig = {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
      ...options
    };
    const response = await fetch(url, requestConfig);
    return handleResponse(response, { url, ...requestConfig });
  },

  put: async (endpoint, data, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const requestConfig = {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
      ...options
    };
    const response = await fetch(url, requestConfig);
    return handleResponse(response, { url, ...requestConfig });
  },

  patch: async (endpoint, data, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const requestConfig = {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
      ...options
    };
    const response = await fetch(url, requestConfig);
    return handleResponse(response, { url, ...requestConfig });
  },

  delete: async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const requestConfig = {
      method: 'DELETE',
      headers: getAuthHeaders(),
      ...options
    };
    const response = await fetch(url, requestConfig);
    return handleResponse(response, { url, ...requestConfig });
  }
};

// Error handling utility (kept for backward compatibility)
export const handleApiError = (error) => {
  console.error('[API] Request failed:', error);
  
  // Note: 401 errors are now handled automatically in handleResponse
  // This function is kept for any manual error handling needs
  if (error.status === 401 && !tokenUtils.getRefreshToken()) {
    // Only clear if no refresh token available
    tokenUtils.clearTokens();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  
  return error;
};

export default apiClient;
